/**
 * Formatting. Bulgarian locale everywhere.
 *
 * The server sends integer minor units plus the currency's `decimals`; it
 * deliberately does NOT send a pre-rendered string. Formatting lives here so
 * there is one place that decides how money looks, and so the client can
 * format the same number differently in different contexts (compact on a card,
 * full in a table) without another round trip.
 */

const LOCALE = 'bg-BG';

const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€',
  BGN: 'лв',
  GBP: '£',
  TRY: '₺',
  USD: '$',
};

export function currencySymbol(code: string): string {
  return CURRENCY_SYMBOLS[code] ?? code;
}

/** 123456 @2 -> "1 234,56" (no currency). */
export function formatAmount(minor: number, decimals = 2): string {
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(minor / 10 ** decimals);
}

/** 123456 @2 EUR -> "1 234,56 €". */
export function formatMoney(minor: number, currency: string, decimals = 2): string {
  return `${formatAmount(minor, decimals)} ${currencySymbol(currency)}`;
}

/**
 * Signed rendering with an explicit + on positives.
 * Used wherever the direction of a movement is the point, so a reader never
 * has to infer it from colour alone — colour is not available to everyone.
 */
export function formatSigned(minor: number, currency: string, decimals = 2): string {
  const sign = minor > 0 ? '+' : minor < 0 ? '−' : '';
  return `${sign}${formatAmount(Math.abs(minor), decimals)} ${currencySymbol(currency)}`;
}

/** "1,2 хил. €" for tight spaces like sparkline axes. */
export function formatCompact(minor: number, currency: string, decimals = 2): string {
  const value = minor / 10 ** decimals;
  return `${new Intl.NumberFormat(LOCALE, { notation: 'compact', maximumFractionDigits: 1 }).format(value)} ${currencySymbol(currency)}`;
}

/**
 * The canonical string sent back to the API.
 *
 * Always a plain dot decimal with no grouping, because the server rejects
 * ambiguous input like "1.500" rather than guessing. Whatever the user typed,
 * this is what goes on the wire.
 */
export function toCanonicalAmount(minor: number, decimals: number): string {
  return (minor / 10 ** decimals).toFixed(decimals);
}

/**
 * Parse what the user typed into minor units.
 *
 * Mirrors the server's rules deliberately — same acceptance, same refusal to
 * guess on an ambiguous three-digit group — so the form can show the error
 * inline instead of round-tripping to find out.
 */
export class AmountError extends Error {}

export function parseAmount(input: string, decimals: number): number {
  const cleaned = input.trim().replace(/[\s  ']/g, '');
  if (cleaned === '') throw new AmountError('Въведи сума');
  if (!/^[-+]?[\d.,]+$/.test(cleaned)) throw new AmountError('Само цифри и разделител');

  const sign = cleaned.startsWith('-') ? -1 : 1;
  const body = cleaned.replace(/^[-+]/, '');

  const dots = (body.match(/\./g) ?? []).length;
  const commas = (body.match(/,/g) ?? []).length;
  const lastSep = Math.max(body.lastIndexOf('.'), body.lastIndexOf(','));

  let normalised: string;
  if (lastSep === -1) {
    normalised = body;
  } else if (dots > 0 && commas > 0) {
    normalised = `${body.slice(0, lastSep).replace(/[.,]/g, '')}.${body.slice(lastSep + 1)}`;
  } else if (dots > 1 || commas > 1) {
    normalised = body.replace(/[.,]/g, '');
  } else {
    const tail = body.slice(lastSep + 1);
    if (tail.length >= 1 && tail.length <= decimals) {
      normalised = `${body.slice(0, lastSep)}.${tail}`;
    } else if (tail.length === 3) {
      throw new AmountError('Двусмислена сума — напиши я без разделител за хиляди');
    } else {
      throw new AmountError(`Най-много ${decimals} знака след запетаята`);
    }
  }

  const value = Number(normalised);
  if (!Number.isFinite(value)) throw new AmountError('Невалидна сума');

  const minor = Math.round(value * 10 ** decimals) * sign;
  if (Math.abs(minor) > 2_147_483_647) throw new AmountError('Сумата е твърде голяма');
  return minor;
}

// ─────────────────────────────── dates ───────────────────────────────

/** "2026-08-05" -> "05.08.2026". */
export function formatDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}.${m}.${y}`;
}

/** "05.08" — for dense lists where the year is obvious from context. */
export function formatDateShort(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split('-');
  return `${d}.${m}`;
}

/** "днес" / "вчера" / "05.08.2026" — relative only where it genuinely helps. */
export function formatDateRelative(iso: string): string {
  const date = iso.slice(0, 10);
  const now = new Date();
  const todayIso = toIsoDate(now);
  if (date === todayIso) return 'днес';

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date === toIsoDate(yesterday)) return 'вчера';

  return formatDate(date);
}

const MONTHS_BG = [
  'януари', 'февруари', 'март', 'април', 'май', 'юни',
  'юли', 'август', 'септември', 'октомври', 'ноември', 'декември',
];

const MONTHS_BG_SHORT = ['яну', 'фев', 'мар', 'апр', 'май', 'юни', 'юли', 'авг', 'сеп', 'окт', 'ное', 'дек'];

/** "2026-08" -> "август 2026". */
export function formatMonth(key: string): string {
  const [y, m] = key.split('-');
  const idx = Number(m) - 1;
  return `${MONTHS_BG[idx] ?? m} ${y}`;
}

/** "2026-08" -> "авг" — chart axes. */
export function formatMonthShort(key: string): string {
  const idx = Number(key.split('-')[1]) - 1;
  return MONTHS_BG_SHORT[idx] ?? key;
}

export function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function todayIso(): string {
  return toIsoDate(new Date());
}

/** "от 3 дни" — the loan cards' nag. */
export function formatAge(days: number): string {
  if (days <= 0) return 'днес';
  if (days === 1) return 'от 1 ден';
  return `от ${days} дни`;
}

/** Exchange rates: enough precision to be useful, not enough to be noise. */
export function formatRate(rate: string | number): string {
  const n = typeof rate === 'string' ? Number(rate) : rate;
  if (!Number.isFinite(n)) return String(rate);
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: n < 1 ? 5 : 4,
  }).format(n);
}

export function formatPercent(value: number): string {
  return new Intl.NumberFormat(LOCALE, {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(value);
}
